import moment from 'moment-timezone';
import { ApolloError, NetworkStatus } from '@apollo/client';
import { PoNumberDisplay } from '@/enums';
import { useCallback, useMemo } from 'react';
import { useSalesPortalSupplierMetadataQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-supplier-metadata.generated';

/**
 * Loader hook for fetching Sales Portal supplier metadata used to drive
 * small UI/behavior toggles (e.g. PO number display mode, whether to show order notes).
 *
 * Data source: `useSalesPortalSupplierMetadataQuery` (GraphQL).
 *
 * Fallback behavior:
 * - If `supplierUUID` is falsy or the query returns no metadata, the hook returns
 *   safe defaults (`poNumberDisplayMode: Hide`, `showOrderNotes: false`). Callers
 *   that gate on the metadata should check `error` first, because those defaults
 *   are also what a failed query returns.
 *
 *
 * @example
 * ```ts
 * const { poNumberDisplayMode, showOrderNotes } = useSalesPortalSupplierMetadataLoader({
 *   supplierUUID,
 * });
 *
 * if (showOrderNotes) {
 *   // render Order Notes UI
 * }
 * ```
 */
interface SalesPortalSupplierMetadataLoaderParams {
  supplierUUID: string;
}

/**
 * Normalized supplier metadata returned by {@link useSalesPortalSupplierMetadataLoader}.
 */
interface SalesPortalSupplierMetadata {
  countryCode: string;
  error: ApolloError | undefined;
  loading: boolean;
  poNumberDisplayMode: PoNumberDisplay;
  refetch: () => Promise<unknown>;
  refetching: boolean;
  showOrderNotes: boolean;
  timezone: string;
}

/**
 * Fetches supplier-level dynamic configuration for Sales Portal ordering experiences.
 *
 * @param params.supplierUUID - Supplier UUID to fetch metadata for (when missing, the query is skipped)
 *
 * @example
 * ```ts
 * const { poNumberDisplayMode, showOrderNotes } = useSalesPortalSupplierMetadataLoader({
 *   supplierUUID,
 * });
 *
 * if (showOrderNotes) {
 *   // render Order Notes UI
 * }
 */
export const useSalesPortalSupplierMetadataLoader = ({
  supplierUUID,
}: SalesPortalSupplierMetadataLoaderParams): SalesPortalSupplierMetadata => {
  const { data, error, loading, networkStatus, refetch } =
    useSalesPortalSupplierMetadataQuery({
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      skip: !supplierUUID,
      variables: {
        supplierUUID,
      },
    });

  const retry = useCallback(() => refetch().catch(() => {}), [refetch]);
  const refetching = networkStatus === NetworkStatus.refetch;

  return useMemo((): SalesPortalSupplierMetadata => {
    const metadata = data?.businesses_by_pk?.supplier_metadata;
    const timezone =
      data?.businesses_by_pk?.address?.timezone ?? moment.tz.guess();
    const countryCode = data?.businesses_by_pk?.address?.country_code ?? '';

    if (!metadata) {
      return {
        countryCode,
        error,
        loading,
        poNumberDisplayMode: PoNumberDisplay.Hide,
        refetch: retry,
        refetching,
        showOrderNotes: false,
        timezone,
      };
    }

    return {
      countryCode,
      error,
      loading,
      poNumberDisplayMode: metadata.po_number_display as PoNumberDisplay,
      refetch: retry,
      refetching,
      showOrderNotes: metadata.show_order_notes,
      timezone,
    };
  }, [data?.businesses_by_pk, error, loading, refetching, retry]);
};
